// apps/client/src/pages/leads/edm/components/BrokerPicker.tsx
//
// Broker filter for the eDM audience (S3) and the roster the "Send as" select
// uses. Ids are Auth UIDs from getBrokerLabels (the same id space as
// leads.assignedBrokerId and contacts.brokerId); the server bridges them to
// client broker ids and drops any that are no longer active members
// (counted as brokerUnmatched). No email is ever returned to the client.

import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { X } from 'lucide-react';
import { functions } from '../../../../lib/firebase';
import { EDM_UI } from '../../../../utils/edm';

export interface EdmBrokerOption {
  id: string;
  displayName: string;
  officeIds: string[];
}

/** Server zod cap on audience.brokerUids. */
export const EDM_MAX_BROKER_UIDS = 50;

/**
 * One-shot getBrokerLabels load (same callable and shape LeadRoutingRulesPage
 * uses). A failure degrades to an empty roster: the picker then shows only
 * already-selected ids, which can still be removed.
 */
export function useBrokerLabels(companyId: string | undefined): { brokers: EdmBrokerOption[]; loading: boolean } {
  const [brokers, setBrokers] = useState<EdmBrokerOption[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    const fn = httpsCallable<
      { companyId: string },
      { brokers?: Array<{ id: string; displayName?: string | null; officeIds?: string[] | null }> }
    >(functions, 'getBrokerLabels');
    fn({ companyId })
      .then((res) => {
        if (!active) return;
        const list = (res?.data?.brokers ?? [])
          .filter((b) => typeof b?.id === 'string' && b.id.length > 0)
          .map((b) => ({ id: b.id, displayName: b.displayName?.trim() || b.id, officeIds: b.officeIds ?? [] }));
        list.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setBrokers(list);
      })
      .catch((err) => {
        if (!active) return;
        console.error('[EDM] getBrokerLabels failed (non-fatal)', err);
        setBrokers([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId]);
  return { brokers, loading };
}

const BrokerPicker: React.FC<{
  brokers: EdmBrokerOption[];
  loading: boolean;
  /** null = every broker. */
  selected: string[] | null | undefined;
  onChange: (ids: string[] | null) => void;
  disabled?: boolean;
}> = ({ brokers, loading, selected, onChange, disabled }) => {
  const [filter, setFilter] = useState('');
  const chosen = useMemo(() => selected ?? [], [selected]);
  const byId = useMemo(() => new Map(brokers.map((b) => [b.id, b])), [brokers]);
  const atCap = chosen.length >= EDM_MAX_BROKER_UIDS;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? brokers.filter((b) => b.displayName.toLowerCase().includes(q)) : brokers;
  }, [brokers, filter]);

  const set = (ids: string[]) => onChange(ids.length > 0 ? ids : null);
  const toggle = (id: string) => set(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);

  return (
    <div className="space-y-2" data-testid="edm-broker-picker">
      <span className={EDM_UI.label}>Brokers</span>
      <p className="text-xs text-gray-400">
        Only people assigned to these brokers. Leave empty to include every broker.
      </p>
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((id) => {
            const known = byId.has(id);
            const label = byId.get(id)?.displayName ?? id;
            return (
              <span
                key={id}
                className={`${EDM_UI.chip} gap-1 ${
                  known ? 'border-blue-500/40 bg-blue-500/10 text-blue-200' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                }`}
              >
                <span>{label}</span>
                {!known && <span className="uppercase tracking-wide text-amber-300/80">not in roster</span>}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    className="text-gray-400 hover:text-red-300"
                    aria-label={`Remove broker ${label}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
      {!disabled && (
        <div className="rounded-md border border-gray-700 bg-gray-900/40">
          {brokers.length > 8 && (
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search brokers by name"
              aria-label="Search brokers"
              className="w-full rounded-t-md border-b border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-100 focus:outline-none"
            />
          )}
          <div className="max-h-40 overflow-y-auto p-1">
            {loading ? (
              <div className="px-2 py-3 text-xs text-gray-500">Loading brokers…</div>
            ) : brokers.length === 0 ? (
              <div className="px-2 py-3 text-xs text-gray-500">No brokers found for your company.</div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-3 text-xs text-gray-500">No brokers match your search.</div>
            ) : (
              filtered.map((b) => {
                const checked = chosen.includes(b.id);
                return (
                  <label key={b.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-800">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!checked && atCap}
                      onChange={() => toggle(b.id)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-xs text-gray-200">{b.displayName}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
      {atCap && !disabled && (
        <p className="text-xs text-amber-300">You can choose up to {EDM_MAX_BROKER_UIDS} brokers.</p>
      )}
    </div>
  );
};

export default BrokerPicker;
